import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  ExpandMoreRounded,
  Link as LinkIcon,
  VideoLibrary as VideoIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import {
  Plan,
  PlanAdminPayload,
  PlanCapacity,
  PlanCapacityPayload,
  PlanResource,
} from "../data/models/Plan/PlanModel";
import {
  Questionnaire,
} from "../data/models/Questionnaire/QuestionnaireModel";
import { useAdminPlansPage } from "./hooks/useAdminPlansPage";
import { useUploadResource } from "./hooks/useUploadResource";

type QuestionnaireSummary = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  status: Questionnaire["status"];
  activeVersionLabel: string;
};

type PlanOverview = Plan & {
  questionnaires: QuestionnaireSummary[];
  capacityCount: number;
  resourceCount: number;
};

const PLAN_ACCENTS = ["#032B66", "#0F766E", "#B45309"];

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const emptyPlanForm: PlanAdminPayload = {
  code: "",
  name: "",
  description: "",
  priceUsd: 0,
};

const emptyCapacityForm: PlanCapacityPayload = {
  code: "",
  capacityCode: "",
  capacityName: "",
};

const resourceTypeLabel = (type: PlanResource["resourceType"]) => {
  switch (type) {
    case "VIDEO":
      return "Video";
    case "MANUAL":
      return "Manual";
    case "PDF":
      return "PDF";
    case "LINK":
      return "Enlace";
    default:
      return type;
  }
};

const questionnaireStatusColor = (
  status: Questionnaire["status"],
): "default" | "success" => (status === "ACTIVE" ? "success" : "default");

const resourceIcon = (type: PlanResource["resourceType"]) => {
  if (type === "VIDEO") {
    return <VideoIcon fontSize="small" color="primary" />;
  }

  if (type === "PDF" || type === "MANUAL") {
    return <DescriptionIcon fontSize="small" color="primary" />;
  }

  return <LinkIcon fontSize="small" color="primary" />;
};

function PlanFormDialog({
  open,
  mode,
  value,
  saving,
  error,
  onChange,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: "create" | "edit";
  value: PlanAdminPayload;
  saving: boolean;
  error: string | null;
  onChange: (value: PlanAdminPayload) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const isEdit = mode === "edit";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Editar plan" : "Nuevo plan"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Código"
            value={value.code}
            disabled={isEdit || saving}
            onChange={(event) =>
              onChange({ ...value, code: event.target.value.toUpperCase() })
            }
            helperText={isEdit ? "El código no se edita para no romper relaciones." : ""}
            fullWidth
          />
          <TextField
            label="Nombre"
            value={value.name}
            disabled={saving}
            onChange={(event) => onChange({ ...value, name: event.target.value })}
            fullWidth
          />
          <TextField
            label="Precio USD"
            type="number"
            value={value.priceUsd}
            disabled={saving}
            onChange={(event) =>
              onChange({ ...value, priceUsd: Number(event.target.value) })
            }
            inputProps={{ min: 0, step: 1 }}
            fullWidth
          />
          <TextField
            label="Descripción"
            value={value.description ?? ""}
            disabled={saving}
            onChange={(event) =>
              onChange({ ...value, description: event.target.value })
            }
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear plan"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CapacityFormDialog({
  open,
  value,
  saving,
  error,
  onChange,
  onClose,
  onSubmit,
}: {
  open: boolean;
  value: PlanCapacityPayload;
  saving: boolean;
  error: string | null;
  onChange: (value: PlanCapacityPayload) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Agregar capacidad</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Plan" value={value.code} disabled fullWidth />
          <TextField
            label="Código de capacidad"
            value={value.capacityCode}
            disabled={saving}
            onChange={(event) =>
              onChange({ ...value, capacityCode: event.target.value.toUpperCase() })
            }
            fullWidth
          />
          <TextField
            label="Nombre de capacidad"
            value={value.capacityName}
            disabled={saving}
            onChange={(event) =>
              onChange({ ...value, capacityName: event.target.value })
            }
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Guardando..." : "Crear capacidad"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QuestionnaireRow({ questionnaire }: { questionnaire: QuestionnaireSummary }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        borderColor: "rgba(15, 23, 42, 0.08)",
        bgcolor: "#ffffff",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            noWrap
            title={questionnaire.name}
          >
            {questionnaire.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {questionnaire.code}
          </Typography>
          {questionnaire.description ? (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
              title={questionnaire.description}
              sx={{ mt: 0.25 }}
            >
              {questionnaire.description}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent="flex-end">
          <Chip
            label={questionnaire.status === "ACTIVE" ? "Activo" : "Inactivo"}
            size="small"
            color={questionnaireStatusColor(questionnaire.status)}
            variant={questionnaire.status === "ACTIVE" ? "filled" : "outlined"}
            sx={{ borderRadius: 999 }}
          />
          <Chip
            label={questionnaire.activeVersionLabel}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

function ResourceRow({
  resource,
  onDelete,
}: {
  resource: PlanResource;
  onDelete: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        borderColor: "rgba(15, 23, 42, 0.08)",
        bgcolor: alpha("#032B66", 0.025),
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha("#032B66", 0.08),
            flexShrink: 0,
          }}
        >
          {resourceIcon(resource.resourceType)}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="subtitle2"
              fontWeight={700}
              noWrap
              title={resource.title}
            >
              {resource.title}
            </Typography>
            <Chip
              label={resourceTypeLabel(resource.resourceType)}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 999, height: 24 }}
            />
          </Stack>
          {resource.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              title={resource.description}
              sx={{ mt: 0.25 }}
            >
              {resource.description}
            </Typography>
          ) : null}
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={resource.url}
            sx={{ mt: 0.25, display: "block" }}
          >
            {resource.url}
          </Typography>
        </Box>

        <Tooltip title="Eliminar recurso">
          <IconButton
            color="error"
            onClick={onDelete}
            sx={{
              bgcolor: alpha("#DC2626", 0.04),
              "&:hover": { bgcolor: alpha("#DC2626", 0.12) },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function CapacityCard({
  capacity,
  onAddResource,
  onDeleteResource,
  uploading,
  progress,
}: {
  capacity: PlanCapacity;
  onAddResource: (capacityId: string) => void;
  onDeleteResource: (resource: PlanResource) => void;
  uploading: boolean;
  progress: number;
}) {
  const resources = Array.isArray(capacity.resources) ? capacity.resources : [];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        borderColor: "rgba(15, 23, 42, 0.08)",
        bgcolor: "#ffffff",
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800}>
              {capacity.capacityName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {capacity.capacityCode}
            </Typography>
          </Box>

          <Chip
            label={`${resources.length} recursos`}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          />
        </Stack>

        <Stack spacing={1.25}>
          {resources.length === 0 ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px dashed rgba(15, 23, 42, 0.16)",
                bgcolor: alpha("#032B66", 0.03),
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Todavía no hay recursos en esta capacidad.
              </Typography>
            </Box>
          ) : (
            resources.map((resource) => (
              <ResourceRow
                key={resource.id}
                resource={resource}
                onDelete={() => onDeleteResource(resource)}
              />
            ))
          )}

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            disabled={uploading}
            sx={{
              mt: 0.25,
              textTransform: "none",
              borderRadius: 2,
              alignSelf: "stretch",
            }}
            onClick={() => onAddResource(capacity.id)}
          >
            {uploading ? `Subiendo ${progress.toFixed(0)}%...` : "Agregar recurso"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PlanCard({
  plan,
  accentColor,
  onEdit,
  onAddCapacity,
  onAddResource,
  onDeleteResource,
  onOpenQuestionnaires,
  uploading,
  progress,
}: {
  plan: PlanOverview;
  accentColor: string;
  onEdit: (plan: PlanOverview) => void;
  onAddCapacity: (plan: PlanOverview) => void;
  onAddResource: (capacityId: string) => void;
  onDeleteResource: (resource: PlanResource) => void;
  onOpenQuestionnaires: (plan: PlanOverview) => void;
  uploading: boolean;
  progress: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const capacities = Array.isArray(plan.capacities) ? plan.capacities : [];
  const questionnaires = plan.questionnaires ?? [];

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        overflow: "hidden",
        border: "1px solid rgba(15, 23, 42, 0.10)",
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        bgcolor: expanded ? "#f8fbff" : "#ffffff",
        borderColor: expanded
          ? "rgba(3, 43, 102, 0.28)"
          : "rgba(15, 23, 42, 0.10)",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 20px 40px rgba(15, 23, 42, 0.10)",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          background: `linear-gradient(90deg, ${accentColor} 0%, ${alpha(accentColor, 0.55)} 100%)`,
        }}
      />
      <CardContent
        sx={{
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.75,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-controls={`plan-details-${plan.code}`}
          onClick={() => setExpanded((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setExpanded((value) => !value);
            }
          }}
          sx={{
            cursor: "pointer",
            borderRadius: 3,
            p: 0.25,
            transition: "background-color 0.18s ease",
            "&:hover": {
              bgcolor: alpha(accentColor, 0.03),
            },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6" fontWeight={900} lineHeight={1.1}>
                {plan.name}
              </Typography>
              <Chip
                label={plan.code}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 999, fontWeight: 700 }}
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                display: "-webkit-box",
                WebkitLineClamp: expanded ? 3 : 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: expanded ? 0 : 44,
                lineHeight: 1.65,
              }}
            >
              {plan.description || "Plan sin descripción disponible."}
            </Typography>
          </Box>

          <Stack alignItems="flex-end" spacing={1}>
            <Chip
              label={USD_FORMATTER.format(plan.priceUsd)}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 800,
                borderRadius: 999,
                bgcolor: alpha("#032B66", 0.04),
              }}
            />
            <IconButton
              size="small"
              aria-label={expanded ? "Ocultar detalle" : "Ver detalle"}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((value) => !value);
              }}
              sx={{
                color: "primary.main",
                bgcolor: alpha("#032B66", 0.06),
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease, background-color 0.18s ease",
                "&:hover": {
                  bgcolor: alpha("#032B66", 0.12),
                },
              }}
            >
              <ExpandMoreRounded fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label={`${plan.capacityCount} capacidades`}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          />
          <Chip
            label={`${plan.resourceCount} recursos`}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          />
          <Chip
            label={`${questionnaires.length} cuestionarios`}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 999 }}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
        >
          <Typography variant="caption" color="text.secondary">
            {expanded
              ? "Oculta el detalle para navegar el catálogo más rápido."
              : "Abre la tarjeta para ver capacidades, recursos y cuestionarios."}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((value) => !value);
            }}
            sx={{ textTransform: "none" }}
          >
            {expanded ? "Ocultar detalle" : "Ver detalle"}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={(event) => {
              event.stopPropagation();
              onEdit(plan);
            }}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Editar
          </Button>
          {/* <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(event) => {
              event.stopPropagation();
              onAddCapacity(plan);
            }}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Agregar capacidad
          </Button> */}
        </Stack>

        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          id={`plan-details-${plan.code}`}
        >
          <Divider />

          <Box
            sx={{
            p: 2,
            borderRadius: 3,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            bgcolor: alpha(accentColor, 0.04),
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: questionnaires.length > 0 ? 1.5 : 0 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Cuestionarios relacionados
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {questionnaires.length > 0
                  ? "Estos cuestionarios usan este plan como condición de acceso."
                  : "Todavía no hay cuestionarios vinculados a este plan."}
              </Typography>
            </Box>

            {questionnaires.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOpenQuestionnaires(plan)}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                Ver todos
              </Button>
            ) : null}
          </Stack>

          {questionnaires.length === 0 ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px dashed rgba(15, 23, 42, 0.16)",
                bgcolor: "#ffffff",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Sin cuestionarios asignados por ahora.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {questionnaires.slice(0, 3).map((questionnaire) => (
                <QuestionnaireRow
                  key={questionnaire.id}
                  questionnaire={questionnaire}
                />
              ))}

              {questionnaires.length > 3 ? (
                <Chip
                  label={`+${questionnaires.length - 3} más`}
                  size="small"
                  onClick={() => onOpenQuestionnaires(plan)}
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 999,
                    cursor: "pointer",
                    bgcolor: "#ffffff",
                  }}
                />
              ) : null}
            </Stack>
          )}
        </Box>

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Capacidades del plan
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {plan.capacityCount} bloques configurados
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={1.5}>
            {capacities.length === 0 ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px dashed rgba(15, 23, 42, 0.16)",
                  bgcolor: "#ffffff",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Este plan todavía no tiene capacidades configuradas.
                </Typography>
              </Box>
            ) : (
              capacities.map((capacity) => (
                <CapacityCard
                  key={capacity.id}
                  capacity={capacity}
                  onAddResource={onAddResource}
                  onDeleteResource={onDeleteResource}
                  uploading={uploading}
                  progress={progress}
                />
              ))
            )}
          </Stack>
        </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default function AdminPlansPage() {
  const { uploadResource, uploading, progress } = useUploadResource();
  const {
    plans,
    questionnaires,
    loading,
    pushNewPlanResource,
    deletePlanResource,
    createPlan,
    updatePlan,
    createCapacity,
  } = useAdminPlansPage(true);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedResource, setSelectedResource] = useState<PlanResource | null>(
    null,
  );
  const [selectedPlan, setSelectedPlan] = useState<PlanOverview | null>(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planDialogMode, setPlanDialogMode] = useState<"create" | "edit">(
    "create",
  );
  const [planForm, setPlanForm] = useState<PlanAdminPayload>(emptyPlanForm);
  const [capacityDialogOpen, setCapacityDialogOpen] = useState(false);
  const [capacityForm, setCapacityForm] =
    useState<PlanCapacityPayload>(emptyCapacityForm);
  const [savingPlan, setSavingPlan] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const plansWithRelations = useMemo<PlanOverview[]>(() => {
    const questionnaireMap = new Map<string, QuestionnaireSummary[]>();

    questionnaires.forEach((questionnaire) => {
      const questionnaireSummary: QuestionnaireSummary = {
        id: questionnaire.id,
        code: questionnaire.code,
        name: questionnaire.name,
        description: questionnaire.description,
        status: questionnaire.status,
        activeVersionLabel: questionnaire.activeVersion
          ? `v${questionnaire.activeVersion.versionNumber}`
          : "Sin versión",
      };

      const planCodes = Array.from(
        new Set(
          (questionnaire.planAssignments ?? [])
            .map((assignment) => assignment.planCode?.trim())
            .filter(Boolean),
        ),
      );

      planCodes.forEach((planCode) => {
        const current = questionnaireMap.get(planCode) ?? [];
        if (!current.some((item) => item.id === questionnaireSummary.id)) {
          questionnaireMap.set(planCode, [...current, questionnaireSummary]);
        }
      });
    });

    questionnaireMap.forEach((items, planCode) => {
      questionnaireMap.set(
        planCode,
        [...items].sort((a, b) => {
          if (a.status !== b.status) {
            return a.status === "ACTIVE" ? -1 : 1;
          }
          return a.name.localeCompare(b.name, "es", {
            sensitivity: "base",
          });
        }),
      );
    });

    return plans.map((plan) => {
      const capacities = Array.isArray(plan.capacities) ? plan.capacities : [];
      const resourceCount = capacities.reduce(
        (total, capacity) =>
          total + (Array.isArray(capacity.resources) ? capacity.resources.length : 0),
        0,
      );

      return {
        ...plan,
        questionnaires: questionnaireMap.get(plan.code) ?? [],
        capacityCount: capacities.length,
        resourceCount,
      };
    });
  }, [plans, questionnaires]);

  const linkedQuestionnairesCount = useMemo(() => {
    return questionnaires.filter((questionnaire) =>
      Boolean(questionnaire.planAssignments?.length),
    ).length;
  }, [questionnaires]);

  const totalResources = useMemo(
    () =>
      plansWithRelations.reduce(
        (total, plan) => total + plan.resourceCount,
        0,
      ),
    [plansWithRelations],
  );

  const handleAddResource = (capacityId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,video/*,application/pdf";
    input.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      try {
        const newResource = (await uploadResource(file, capacityId)) as PlanResource;
        await pushNewPlanResource(newResource);
      } catch (error) {
        console.error("Error al subir recurso:", error);
      }
    };
    input.click();
  };

  const openCreatePlanDialog = () => {
    setPlanDialogMode("create");
    setPlanForm(emptyPlanForm);
    setFormError(null);
    setPlanDialogOpen(true);
  };

  const openEditPlanDialog = (plan: PlanOverview) => {
    setPlanDialogMode("edit");
    setPlanForm({
      code: plan.code,
      name: plan.name,
      description: plan.description ?? "",
      priceUsd: plan.priceUsd,
    });
    setFormError(null);
    setPlanDialogOpen(true);
  };

  const openCapacityDialog = (plan: PlanOverview) => {
    setCapacityForm({
      ...emptyCapacityForm,
      code: plan.code,
    });
    setFormError(null);
    setCapacityDialogOpen(true);
  };

  const handleSubmitPlan = async () => {
    const cleanPayload = {
      ...planForm,
      code: planForm.code.trim().toUpperCase(),
      name: planForm.name.trim(),
      description: planForm.description?.trim() ?? "",
      priceUsd: Number(planForm.priceUsd),
    };

    if (!cleanPayload.code || !cleanPayload.name || !Number.isFinite(cleanPayload.priceUsd)) {
      setFormError("Código, nombre y precio son obligatorios.");
      return;
    }
    if (cleanPayload.priceUsd < 0) {
      setFormError("El precio no puede ser negativo.");
      return;
    }

    try {
      setSavingPlan(true);
      setFormError(null);
      if (planDialogMode === "create") {
        await createPlan(cleanPayload);
      } else {
        await updatePlan(cleanPayload);
      }
      setPlanDialogOpen(false);
    } catch (error: any) {
      setFormError(error?.message ?? "No se pudo guardar el plan.");
    } finally {
      setSavingPlan(false);
    }
  };

  const handleSubmitCapacity = async () => {
    const cleanPayload = {
      code: capacityForm.code.trim().toUpperCase(),
      capacityCode: capacityForm.capacityCode.trim().toUpperCase(),
      capacityName: capacityForm.capacityName.trim(),
    };

    if (!cleanPayload.code || !cleanPayload.capacityCode || !cleanPayload.capacityName) {
      setFormError("Plan, código y nombre de capacidad son obligatorios.");
      return;
    }

    try {
      setSavingPlan(true);
      setFormError(null);
      await createCapacity(cleanPayload);
      setCapacityDialogOpen(false);
    } catch (error: any) {
      setFormError(error?.message ?? "No se pudo crear la capacidad.");
    } finally {
      setSavingPlan(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "55vh",
          display: "grid",
          placeItems: "center",
          p: 3,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Cargando planes y cuestionarios...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h4" fontWeight={800} color="text.primary">
            Planes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona recursos, capacidades y cuestionarios vinculados desde un
            panel centralizado.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreatePlanDialog}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Nuevo plan
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 4,
          border: "1px solid rgba(15, 23, 42, 0.08)",
          bgcolor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={`${plansWithRelations.length} planes`}
              variant="outlined"
              sx={{ borderRadius: 999 }}
            />
            <Chip
              label={`${linkedQuestionnairesCount} cuestionarios vinculados`}
              variant="outlined"
              sx={{ borderRadius: 999 }}
            />
            <Chip
              label={`${totalResources} recursos`}
              variant="outlined"
              sx={{ borderRadius: 999 }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Abre cada tarjeta para revisar detalles sin saturar la vista cuando
            crezca el catálogo.
          </Typography>
        </Stack>
      </Paper>

      {plansWithRelations.length === 0 ? (
        <Alert severity="info">Todavía no hay planes disponibles.</Alert>
      ) : (
        <Grid container spacing={3}>
          {plansWithRelations.map((plan, index) => (
            <Grid item xs={12} sm={6} lg={4} key={plan.code}>
              <PlanCard
                plan={plan}
                accentColor={PLAN_ACCENTS[index % PLAN_ACCENTS.length]}
                onEdit={openEditPlanDialog}
                onAddCapacity={openCapacityDialog}
                onAddResource={handleAddResource}
                onDeleteResource={(resource) => {
                  setSelectedResource(resource);
                  setOpenConfirm(true);
                }}
                onOpenQuestionnaires={(currentPlan) => setSelectedPlan(currentPlan)}
                uploading={uploading}
                progress={progress}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={Boolean(selectedPlan)}
        onClose={() => setSelectedPlan(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={900}>
                {selectedPlan?.name || "Plan"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlan?.code}
              </Typography>
            </Box>

            <IconButton onClick={() => setSelectedPlan(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#f8fbff" }}>
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                borderColor: "rgba(15, 23, 42, 0.08)",
                bgcolor: "#ffffff",
              }}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`${selectedPlan?.capacityCount ?? 0} capacidades`} />
                <Chip label={`${selectedPlan?.resourceCount ?? 0} recursos`} />
                <Chip
                  label={`${selectedPlan?.questionnaires?.length ?? 0} cuestionarios`}
                />
                <Chip label={USD_FORMATTER.format(selectedPlan?.priceUsd ?? 0)} />
              </Stack>
            </Paper>

            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                Cuestionarios vinculados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Aquí se muestran todos los cuestionarios que tienen este plan en
                sus reglas de acceso.
              </Typography>

              <Stack spacing={1.25}>
                {(selectedPlan?.questionnaires ?? []).length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    Este plan aún no tiene cuestionarios asignados.
                  </Alert>
                ) : (
                  selectedPlan?.questionnaires?.map((questionnaire) => (
                    <QuestionnaireRow
                      key={questionnaire.id}
                      questionnaire={questionnaire}
                    />
                  ))
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            component={RouterLink}
            to="/dashboard/admin/questionnaires"
            startIcon={<DescriptionIcon />}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Ir a cuestionarios
          </Button>
          <Button onClick={() => setSelectedPlan(null)} sx={{ textTransform: "none" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <PlanFormDialog
        open={planDialogOpen}
        mode={planDialogMode}
        value={planForm}
        saving={savingPlan}
        error={planDialogOpen ? formError : null}
        onChange={setPlanForm}
        onClose={() => setPlanDialogOpen(false)}
        onSubmit={handleSubmitPlan}
      />

      <CapacityFormDialog
        open={capacityDialogOpen}
        value={capacityForm}
        saving={savingPlan}
        error={capacityDialogOpen ? formError : null}
        onChange={setCapacityForm}
        onClose={() => setCapacityDialogOpen(false)}
        onSubmit={handleSubmitCapacity}
      />

      <ConfirmationDialog
        open={openConfirm}
        title="Eliminar recurso"
        message={`¿Seguro que deseas eliminar "${selectedResource?.title}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onClose={() => setOpenConfirm(false)}
        onConfirm={async () => {
          if (selectedResource) {
            await deletePlanResource(selectedResource);
          }
          setOpenConfirm(false);
          setSelectedResource(null);
        }}
      />
    </Box>
  );
}
